import React, { Component } from "react";
import {connect} from 'react-redux';
import { reduxForm, Field, change } from "redux-form";
import { BlueBtn } from "components/Controls";
import { breadcrumbMap, getForms } from "../constants";
import Breadcrumb from "components/Breadcrumb";
import { onlyNumbers } from "utils/normalize";
import { logPaypairConsentEvents } from "utils/paypair_events";
import { required, isNumEqual9, isBankAccountNumber, validate } from "utils/validations";
import { normalizeSNN } from "utils/normalize";
import { initApplication, continueInitApplication, setKoalafiNeuroId } from "../actions";
import { generateNeuroIdentity, identifyNeuroId } from "../neuro_id_helper";
import {
  appRequestSelector,
  loadingSelector,
  formValuesSelector,
  affiliatedProviderSelector,
  trendingsVisitID, activeProviders, currentMerchant,
  affAgreements, b2bSelector, activeStepDataSelector
} from '../selectors';
import { formValueSelector } from "redux-form";
import { Submitted } from "components/FlexshopperMetrics";
import { AnalyticAPI } from "api/analytic";
import { ApplicationRequestAPI } from "api/application_request";
import { tokenSelector } from "containers/EmbeddableWidget/selectors";
import Secure from "assets/img/lock_secure.png";
import { IconBank } from "components/Svgs";
// TODO: move to common dir
import { renderField } from "./input_helpers";
import { renderPrivacyPolicy } from "../privacy_policy";
import { renderHeader } from "../header";
import "./styles.scss";
import FinancialInfoForm from "components/FinancialInfoForm";
import DateOfBirth from "components/Fields/DateOfBirth";
import {logEvent} from '../../EmbeddableWidget/actions';
import PersonalInfoForm from '../../../components/PersonalInfoForm';
import {BUSINESS_OWNER_INFO_TITLE, BANK_ACCOUNT_TITLE, BANK_ACCOUNT_TEXT} from './constants';

const selector = formValueSelector("form");

class Step2 extends Component {
  state = {
    showPayoutDayOption: false,
    payoutDay: "first",
    payFrequency: "",
    clearLastPaydate: false,
    nextPaycheckDate: null,
    addBankAccount: false,
  };

  componentDidMount() {
    const { token, visitID, b2b, setKoalafiNeuroId } = this.props;
    AnalyticAPI.send_analytic_event(token, "page2", visitID);

    if (!b2b) {
      const preInitData = this.buildPreInitData();
      ApplicationRequestAPI.koalafiCreate(token, preInitData)
        .then((data) => (data && data.koalafi && data.koalafi.id) || null)
        .catch(() => null)
        .then((koalafiId) => {
          // Koalafi's pre-init id is the NeuroID identity when available; without
          // Koalafi (merchant not configured / call failed) we still need one for
          // the other NeuroID consumers (Uown, AFF), so generate it here.
          const neuroIdentity = koalafiId || generateNeuroIdentity();
          setKoalafiNeuroId(neuroIdentity);
          identifyNeuroId(neuroIdentity);

          if (!koalafiId) return;
          const script = document.createElement("script");
          script.setAttribute(
            "src",
            `https://content.koalafi.com/fp/tags.js?org_id=${process.env.KOALAFI_ORG_ID}&session_id=${koalafiId}`
          );
          document.head.appendChild(script);
        });
    }
  }

  submitForm = async (values) => {
    const {logEvent, initApplication} = this.props;
    const normalizedPhone = this.props.appRequest.personalInfo.phone && onlyNumbers(this.props.appRequest.personalInfo.phone);

    initApplication({ values: this.buildBodyToInitApplication() });
    logEvent({name: 'see payment plans'})
    await logPaypairConsentEvents({
      logEvent,
      phone: normalizedPhone,
      smsMarketingConsent: values.agreeTcpa ?? false,
    });
  };

  buildPreInitData = () => {
    const { appRequest } = this.props;

    return {
      application: {
        personalInfo: {
          phone: appRequest.personalInfo.phone && onlyNumbers(appRequest.personalInfo.phone),
          firstName: appRequest.personalInfo.firstName,
          lastName: appRequest.personalInfo.lastName,
          email: appRequest.personalInfo.email,
        },
        shipping: appRequest.shipping,
        items: appRequest.items,
        orderId: appRequest.orderId,
      },
    };
  };

  buildBodyToInitApplication = () => {
    const { appRequest, values, b2b, providerName } = this.props;
    const { nextPaycheckDate } = this.state;

    if (b2b) {
      // B2B flow
      return {
        provider_name: providerName,
        personalInfo: {
          phone: appRequest.personalInfo.phone && onlyNumbers(appRequest.personalInfo.phone),
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          dob: `${values.year}-${values.month}-${values.day}`,
          ssn: values.ssn && onlyNumbers(values.ssn),
        },
        residenceInfo: {
          street: values.streetAddress,
          country: "US",
          city: values.city,
          state: values.state,
          zip: values.zipCode,
        },
      };
    }

    // non-B2B flow
    const applicationRequest = {
      provider_name: providerName === 'mybambu' ? 'ownlease' : providerName,
      personalInfo: {
        phone: appRequest.personalInfo.phone && onlyNumbers(appRequest.personalInfo.phone),
        dob: `${values.year}-${values.month}-${values.day}`,
        ssn: providerName === 'mybambu' ? '999999999' : values.ssn && onlyNumbers(values.ssn),
      },
      consents: {
        agreeTcpa: values.agreeTcpa || false,
      },
      employmentInformation: {
        payFrequency: values.payFrequency,
        monthlyIncome: values.income && parseFloat(onlyNumbers(values.income)),
        last_payday_on: values.lastPaycheck,
        next_payday_on: nextPaycheckDate,
        direct_deposit: "true",
      },
      ...(providerName === 'mybambu' && { mybambu: true }),
    };

    const bankAccount = this.bankAccountData();
    if (appRequest.provider_name === "flexshopper" || bankAccount) {
      applicationRequest.has_additional_info = true;
      applicationRequest.additional_info_data = bankAccount ? { bankAccount } : {};
    }
    return applicationRequest;
  };

  // Bank account from the optional block: only while the switch is on and both
  // fields are filled (they are required while the switch is on). It goes to
  // the lenders that take it with the application (Uown, Progressive, Snap).
  bankAccountData = () => {
    const { values } = this.props;
    const { addBankAccount } = this.state;
    const { bankRouting, bankAccount } = values || {};

    if (!addBankAccount || !bankRouting || !bankAccount) {
      return null;
    }
    return {
      routing: onlyNumbers(bankRouting),
      account: onlyNumbers(bankAccount),
      type: "checking",
    };
  };

  toggleBankAccount = () => {
    const { change } = this.props;
    const addBankAccount = !this.state.addBankAccount;

    if (!addBankAccount) {
      // the fields unmount; drop their values so the submit sends no bank data
      change("bankRouting", "");
      change("bankAccount", "");
    }
    this.setState({ addBankAccount });
  };

  renderBankAccount = () => {
    const { addBankAccount } = this.state;

    return (
      <div className="step2__bank-account">
        <div className="step2__bank-account-header">
          <IconBank />
          <div className="step2__bank-account-heading">
            <div className="step2__bank-account-title">
              {BANK_ACCOUNT_TITLE}
              <span className="step2__bank-account-badge">Optional</span>
            </div>
            <div className="step2__bank-account-text">{BANK_ACCOUNT_TEXT}</div>
          </div>
          <label className="step2__bank-account-switch">
            <input
              type="checkbox"
              checked={addBankAccount}
              onChange={this.toggleBankAccount}
            />
            <span className="step2__bank-account-slider" />
          </label>
        </div>
        {addBankAccount && (
          <div className="step2__bank-account-fields">
            <div className="form-item-group">
              <div className="form-item">
                <Field
                  name="bankRouting"
                  validate={[required, isNumEqual9]}
                  component={renderField}
                  normalize={onlyNumbers}
                />
              </div>
              <div className="form-item">
                <Field
                  name="bankAccount"
                  validate={[required, isBankAccountNumber]}
                  component={renderField}
                  normalize={onlyNumbers}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  renderForm = () => {
    const { month, day, year, providerName, change, b2b } = this.props;

    return (
      <div>
        {b2b ? (
          <div className="personal-info-box">
            <div className="personal-info"><strong>{BUSINESS_OWNER_INFO_TITLE}</strong></div>
            <PersonalInfoForm month={month} day={day} year={year} change={change}/>
          </div>
        ) : (
          <>
            <div className="step2__form paypair-form step2-border_box">
              <div className="requeired-field_text">* Required field</div>
              <div className="form-item-group">
                <div className={`date-of-birthday form-item ${providerName === 'mybambu' && 'full-width'}`}>
                  <DateOfBirth
                    month={month}
                    day={day}
                    year={year}
                    resetDay={() => change('day', '')}
                  />
                </div>
                {providerName !== 'mybambu' && (
                  <div className="form-item form-item--ssn">
                    <Field
                      name="ssn"
                      validate={[required, isNumEqual9]}
                      component={renderField}
                      normalize={normalizeSNN}
                    />
                  </div>
                )}
              </div>
              <FinancialInfoForm
                setValue={this.setState.bind(this)}
                payoutDay={this.state.payoutDay}
                values={this.props}
                clearLastPaydate={this.state.clearLastPaydate}
                showPayoutDayOption={this.state.showPayoutDayOption}
                payoutFrequency={this.state.payoutFrequency}
              />
              {this.renderBankAccount()}
            </div>
            <div className="div-lock-green">
              <div>
                <img src={Secure} className="lock-img"/>
              </div>
              <span className="lock-green">
                Your information is encrypted and securely shared with our financing partners for application and verification purposes.
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  render() {
    const {
      valid,
      loading,
      handleSubmit,
      token,
      providerName,
      activeProviders,
      affAgreements,
      merchant,
      b2b,
      activeStepData
    } = this.props;

    const {nextPaycheckDate} = this.state;

    const FORMS = getForms();

    return (
      <form onSubmit={handleSubmit(this.submitForm)} className="step2">
        {renderHeader()}
        <Breadcrumb
          data={breadcrumbMap}
          activeBreadcrumb={'Application Details'}
        />
        <div className="step2__content flex flex--column flex__align--center">
          {this.renderForm()}
          <div className="Line-73"></div>
          {renderPrivacyPolicy(token, activeProviders, affAgreements, merchant)}
          <div className="paypair-form paypair-form-padding">
            <div className="form-item">
              {loading && providerName === 'flexshopper' && <Submitted/>}
            </div>
          </div>
          <div className=" paypair-form-padding">
            <BlueBtn
              customClass="step2__submit-btn payment-plan_button"
              disabled={b2b ? !valid : (!valid || !nextPaycheckDate)}
              loading={loading}
              type="submit"
            >
              See My Purchase Options
            </BlueBtn>
          </div>
        </div>
      </form>
    );
  }
}

const mapStateToProps = (state) => ({
  token: tokenSelector()(state),
  values: formValuesSelector()(state),
  loading: loadingSelector()(state),
  appRequest: appRequestSelector()(state),
  activeProviders: activeProviders()(state),
  providerName: affiliatedProviderSelector()(state),
  b2b: b2bSelector()(state),
  month: selector(state, "month"),
  day: selector(state, "day"),
  year: selector(state, "year"),
  visitID: trendingsVisitID()(state),
  affAgreements: affAgreements()(state),
  merchant: currentMerchant()(state),
  activeStepData: activeStepDataSelector()(state)
});

const mapDistpatchToProps = (dispatch) => {
  return {
    initApplication: (values) => dispatch(initApplication(values)),
    continueInitApplication: (values) =>
      dispatch(continueInitApplication(values)),
    logEvent: (value) => dispatch(logEvent(value)),
    setKoalafiNeuroId: (value) => dispatch(setKoalafiNeuroId(value)),
  };
};

Step2 = connect(mapStateToProps, mapDistpatchToProps)(Step2);

Step2 = reduxForm({
  validate,
  form: "form",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(Step2);

export default Step2;
